import { ScopeButton } from '@monorepo/shared-lib';
import { Space, Tooltip } from 'antd';
import LoadTaxesPic from 'assets/images/load-taxes-pic.svg';
import { StyledCard } from 'components/Custom/card';
import { CardDescSpan, CardTitleLargeSpan } from 'components/Custom/spans';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { getChartOfAccountsLoading } from 'store/chart-of-accounts/selectors';
import type { Country } from 'store/common/models';
import { getCommonCountries } from 'store/common/selectors';
import { getIs3rdPartyProvider } from 'utils/get-provider-type';
import { AddChartOfAccountsForm } from './add-chart-of-accounts-form';
import { PromotionMessage } from './promotion-message';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';

interface ChartOfAccountsPromotionBannerProps {
  organisation?: any;
  fullWidth?: boolean;
  title?: string;
  message?: React.ReactNode;
  /** Width of the side card when not fullWidth — same styling as TaxRatesPromotionBanner side card. */
  cardWidth?: number;
  /** Same as TaxRatesPromotionBanner: fill fixed narrow side panel exactly. */
  containedInFixedPanel?: boolean;
}

export const ChartOfAccountsPromotionBanner: React.FC<
  ChartOfAccountsPromotionBannerProps
> = ({
  organisation,
  fullWidth = false,
  title,
  message,
  cardWidth = TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
  containedInFixedPanel = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const gettingChartOfAccounts = useSelector(getChartOfAccountsLoading);
  const countries = useSelector(getCommonCountries) as Country[];
  const hasExistingAccounts =
    Array.isArray(organisation?.chartOfAccounts) &&
    organisation.chartOfAccounts.length > 0;
  const buttonLabel = hasExistingAccounts
    ? t('organisation.coa-promotion-add-other-accounts')
    : t('common.proceed');

  /** Excel / Receipts only: bulk COA wizard is disabled for integrations (e.g. Xero). */
  const isThirdPartyProvider = getIs3rdPartyProvider(
    organisation?.providerName
  );

  /** Allow opening the flow only once accounts have been loaded onto the entity (array, possibly empty). */
  const coaNotReady =
    !organisation?.uuid || !Array.isArray(organisation?.chartOfAccounts);

  const proceedDisabled = coaNotReady || isThirdPartyProvider;

  const proceedDisabledTitle = coaNotReady
    ? t('organisation.coa-promotion-loading-tooltip')
    : isThirdPartyProvider
    ? t('organisation.coa-promotion-provider-restriction-tooltip')
    : undefined;

  const onProceed = () => {
    if (proceedDisabled) return;
    // Prefer a fixed Malta country (iso === 'MT') for COA templates
    const malta = Array.isArray(countries)
      ? countries.find((c) => c.iso === 'MT')
      : undefined;
    const maltaCountryId = malta?.id;

    // Fallback: if Malta is missing, retain original behaviour
    const entityCountryId =
      maltaCountryId ?? organisation?.returnTypesEdit?.[0]?.countryId;

    const drawerData: DrawerData = {
      title: t('organisation.add-account'),
      type: DrawerType.ADD_CHART_OF_ACCOUNTS,
      width: 560,
      component: (
        <AddChartOfAccountsForm
          loading={gettingChartOfAccounts}
          organisationId={organisation?.uuid}
          initialCountryId={entityCountryId}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const defaultMessage = (
    <PromotionMessage i18nKey="organisation.coa-promotion-message" />
  );

  const displayMessage = message ?? defaultMessage;

  if (fullWidth) {
    return (
      <StyledCard
        $nopadding
        fullsize
        style={{
          boxShadow: '0px 4px 10px 0px #0000000D',
          marginBottom: '20px',
          display: 'flex',
          fontSize: 14,
          fontWeight: 400,
          padding: 0,
        }}
        width="100%"
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexFlow: 'row',
            alignContent: 'end',
            padding: '24px 24px 0 0',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexFlow: 'column',
              justifyContent: 'space-between',
              padding: '0 24px 24px 24px',
            }}
          >
            {title && (
              <Space>
                <CardTitleLargeSpan style={{ marginBottom: 10 }}>
                  {title}
                </CardTitleLargeSpan>
              </Space>
            )}
            <CardDescSpan style={{ marginBottom: 16 }}>
              {displayMessage}
            </CardDescSpan>
            <Tooltip title={proceedDisabledTitle}>
              <span style={{ display: 'inline-block' }}>
                <ScopeButton
                  type="dark-orange"
                  width="180px"
                  height="40px"
                  fontSize="15px"
                  lineheight="12px"
                  onClick={onProceed}
                  disabled={proceedDisabled}
                >
                  {buttonLabel}
                </ScopeButton>
              </span>
            </Tooltip>
          </div>

          <img alt={t('organisation.chart-of-accounts')} src={LoadTaxesPic} />
        </div>
      </StyledCard>
    );
  }

  const sideCardStyle: React.CSSProperties = containedInFixedPanel
    ? {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        height: 'fit-content',
      }
    : {
        width: cardWidth,
        height: 'fit-content',
        boxSizing: 'border-box',
      };

  const innerChrome = containedInFixedPanel
    ? ({
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      } as React.CSSProperties)
    : {};

  const imgStyle: React.CSSProperties = containedInFixedPanel
    ? { width: '100%', maxWidth: 250, height: 'auto', display: 'block' }
    : { width: 250, height: 'auto', display: 'block' };

  const textStyle: React.CSSProperties = containedInFixedPanel
    ? {
        fontSize: 18,
        fontWeight: 400,
        lineHeight: '24px',
        maxWidth: '100%',
        wordBreak: 'break-word',
      }
    : { fontSize: 18, fontWeight: 400, lineHeight: '24px' };

  return (
    <StyledCard style={sideCardStyle}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          alignItems: 'center',
          ...innerChrome,
        }}
      >
        <img src={LoadTaxesPic} alt={t('organisation.chart-of-accounts')} style={imgStyle} />

        <span style={textStyle}>
          <PromotionMessage i18nKey="organisation.coa-promotion-message" />
        </span>
        <Tooltip title={proceedDisabledTitle}>
          <span
            style={{
              display: 'inline-block',
              alignSelf: 'center',
              margin: '0 auto',
            }}
          >
            <ScopeButton
              width="180px"
              height="40px"
              fontSize="15px"
              lineheight="12px"
              type="dark-orange"
              onClick={onProceed}
              disabled={proceedDisabled}
            >
              {buttonLabel}
            </ScopeButton>
          </span>
        </Tooltip>
      </div>
    </StyledCard>
  );
};
